import {lazy, Suspense, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {PushpinFilled, PushpinOutlined} from '@ant-design/icons';
import type {MenuProps} from 'antd';
import {Drawer, Layout, Menu, Tooltip} from 'antd';
import clsx from 'clsx';
import {useAtomValue} from 'jotai';

/* Касса — самый тяжёлый кусок админки: схемы залов, zoom-pan,
   выделение рамкой. Она висит глобальным дровером и открывается с
   любой страницы, но статический импорт затягивал её в каркас — то
   есть в КАЖДЫЙ первый заход, даже если человек пришёл смотреть
   отчёты. Из 339 модулей каркаса 296 приходились на неё одну.
   Грузим по факту открытия. */
import {isNewSaleOpenAtom} from '@atoms/newSale';
import {useAuth} from '@hooks/useAuth';
import {useBreakpoint} from '@hooks/useBreakpoint';
import {usePinnedNav} from '@hooks/usePinnedNav';
import {BrandMark} from '@components/BrandMark';
import {PermissionPickerModal} from '@components/PermissionPickerModal';
import {PersonalizationModal} from '@components/PersonalizationModal';

const NewSale = lazy(() => import('@pages/Sales/NewSale').then((m) => ({default: m.NewSale})));

import {AppHeader} from './AppHeader';
import {
  activeFromPath,
  buildHeaderTitle,
  buildMenuItems,
  buildVisibleSections,
  describePermission,
  findLeafByKey,
  resolveNavTarget,
} from './AppShell.utils';

import styles from './AppShell.module.scss';

const {Sider, Content} = Layout;

export const AppShell = () => {
  /* Открыта ли касса — читаем атом напрямую: хук useNewSale тянет за
     собой действия и типы корзины, а нам нужен один флаг. */
  const saleOpen = useAtomValue(isNewSaleOpenAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const {permission} = useAuth();
  const {isMobile, isTablet} = useBreakpoint();

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const visibleSections = useMemo(() => buildVisibleSections(), []);

  /* ── Закреплённые разделы ─────────────────────────────────────────
     Пин выносит лист наверх меню, чтобы не раскрывать SubMenu каждый
     раз. Кнопка-скрепка живёт в label'е листа: проявляется на hover
     (на тач-устройствах — всегда видна, см. SCSS). */
  const {pinned, isPinned, toggle, isFull} = usePinnedNav();

  const renderLeafLabel = (key: string, title: string) => {
    const active = isPinned(key);
    const blocked = !active && isFull;
    return (
      <span className={styles.leafLabel}>
        <span className={styles.leafTitle}>{title}</span>
        <Tooltip
          title={active ? 'Открепить' : blocked ? 'Достигнут лимит закреплённых' : 'Закрепить'}
          placement="right"
        >
          <span
            role="button"
            tabIndex={-1}
            aria-label={active ? 'Открепить раздел' : 'Закрепить раздел'}
            className={clsx(styles.pinBtn, active && styles.pinBtnActive)}
            data-disabled={blocked || undefined}
            onClick={(e) => {
              // Иначе Menu примет клик за навигацию по пункту.
              e.stopPropagation();
              if (blocked) return;
              toggle(key);
            }}
          >
            {active ? <PushpinFilled /> : <PushpinOutlined />}
          </span>
        </Tooltip>
      </span>
    );
  };

  /** Пункты секции «Закреплённые» — только валидные (домены могли
   *  измениться, permission — пропасть).
   *
   *  Label тот же, что у листа в дереве: со скрепкой. Иначе открепить
   *  можно было бы только найдя оригинальный пункт внутри SubMenu —
   *  ровно то раскрытие, ради ухода от которого пин и делался. */
  const pinnedItems = useMemo(
    () =>
      pinned
        .map((key) => {
          const found = findLeafByKey(key, visibleSections);
          return found
            ? {
                key: `pinned:${key}`,
                icon: found.section.icon,
                label: renderLeafLabel(key, found.leaf.title),
              }
            : null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
    // renderLeafLabel замыкает pinned/isFull — см. ниже.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinned, visibleSections, isFull],
  );

  /**
   * Итоговое меню: закреплённые вклиниваются сразу после «Главной»
   * (первый пункт) — там, где взгляд уже находится, и не приходится
   * скроллить сайдбар вниз. Отдельного Menu нет: это те же пункты в
   * общем списке, только продублированные наверх.
   */
  const menuItems = useMemo<MenuProps['items']>(() => {
    const base = buildMenuItems(visibleSections, renderLeafLabel) ?? [];
    if (pinnedItems.length === 0) return base;
    const [first, ...rest] = base;
    return first ? [first, ...pinnedItems, ...rest] : [...pinnedItems, ...rest];
    // renderLeafLabel замыкает pinned/isFull — пересобираем при их смене.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSections, pinned, isFull, pinnedItems]);

  const activeMatch = useMemo(
    () => activeFromPath(location.pathname, visibleSections),
    [location.pathname, visibleSections],
  );

  const title = buildHeaderTitle(activeMatch);
  const subtitle = describePermission(permission);

  const selectedKeys = activeMatch.leaf
    ? [`${activeMatch.section?.id}/${activeMatch.leaf.id}`]
    : activeMatch.section
      ? [activeMatch.section.id]
      : [];
  const openKeys = activeMatch.section ? [activeMatch.section.id] : [];

  // Закрываем drawer при ресайзе в десктоп.
  useEffect(() => {
    if (!isTablet) setDrawerOpen(false);
  }, [isTablet]);

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    // Пункты секции «Закреплённые» имеют префикс, чтобы AntD не считал
    // их дубликатами оригинальных листов (иначе подсветка ломается).
    const key = info.key.startsWith('pinned:') ? info.key.slice('pinned:'.length) : info.key;
    const target = resolveNavTarget(key, visibleSections);
    if (target) navigate(target);
    setDrawerOpen(false);
  };

  const handleBrandClick = () => {
    navigate('/');
  };

  const handleBrandKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') navigate('/');
  };

  const siderMenu = (
    <Menu
      theme="dark"
      mode="inline"
      items={menuItems}
      // Подсвечиваем и оригинальный пункт, и его закреплённый дубль.
      selectedKeys={[...selectedKeys, ...selectedKeys.map((k) => `pinned:${k}`)]}
      defaultOpenKeys={openKeys}
      onClick={handleMenuClick}
      className={styles.menu}
    />
  );

  return (
    <Layout className={styles.root}>
      {!isTablet && (
        <Sider
          theme="dark"
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={248}
          collapsedWidth={72}
          className={styles.sider}
        >
          <div
            className={clsx(
              styles.siderBrand,
              collapsed ? styles.siderBrandCollapsed : styles.siderBrandExpanded,
            )}
            onClick={handleBrandClick}
            onKeyDown={handleBrandKey}
            role="button"
            tabIndex={0}
          >
            <BrandMark size={28} showWordmark={!collapsed} />
          </div>
          {siderMenu}
        </Sider>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="left"
        width={280}
        closable={false}
        styles={{
          body: {padding: 0, background: 'var(--color-sider-bg)'},
          header: {display: 'none'},
        }}
      >
        <div className={styles.drawerBrand}>
          <BrandMark size={28} showWordmark />
        </div>
        {siderMenu}
      </Drawer>

      <Layout className={styles.contentHost}>
        <AppHeader
          title={title}
          subtitle={subtitle}
          onOpenDrawer={() => setDrawerOpen(true)}
          onOpenPicker={() => setPickerOpen(true)}
        />
        <Content className={clsx(styles.content, isMobile && styles.contentMobile)}>
          <Outlet />
        </Content>
      </Layout>

      <PermissionPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />
      <PersonalizationModal />
      {/* Глобальный drawer кассы — открывается через `useNewSale().open()`
          с любой страницы. State держится в jotai-atom'е.
          Монтируем только когда открыт: до первой продажи её код
          вообще не скачивается. */}
      {saleOpen && (
        <Suspense fallback={null}>
          <NewSale />
        </Suspense>
      )}
    </Layout>
  );
};
